import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import FinancesPayBtns from '../FinancePay/FinancesPayBtns/FinancesPayBtns';
import scrollIntoViewBelowHeader from '../../utils/scrollIntoViewBelowHeader';
import courseTitle from '../../utils/courseTitle';
import { ArrowRight, BooksIcon, CapIcon, CardIcon, InfoIcon } from './icons';
import { lessonsWord, formatMoney } from '../../utils/lessonsWord';

import './finances-v2.scss';
import './pay-v2.scss';

const LABELS = [
  { hours: 8, title: 'Базовий', books: 1 },
  { hours: 16, title: 'Оптимальний', books: 2 },
];

const pickTariff = (tariffs, hours) => (
  tariffs.find((t) => Number(t.show_hours) === hours && !Number(t.is_special))
  || tariffs.find((t) => Number(t.hours) === hours)
  || null
);

const buildCards = (tariffs, fullCourse) => {
  const cards = [];

  LABELS.forEach((label) => {
    const tariff = pickTariff(tariffs, label.hours);
    if (tariff) {
      cards.push({ key: `h${label.hours}`, title: label.title, books: label.books, tariff });
    }
  });

  if (fullCourse) {
    const tariff = tariffs.find((t) => Number(t.hours) === Number(fullCourse.hours));
    if (tariff && !cards.some((card) => card.tariff === tariff)) {
      cards.push({ key: 'full', title: 'До кінця курсу', books: 3, tariff });
    } else if (tariff) {
      const same = cards.find((card) => card.tariff === tariff);
      same.title = 'До кінця курсу';
      same.books = 3;
    }
  }

  if (cards.length < 2) {
    return tariffs.map((tariff, idx) => ({
      key: `t${idx}`,
      title: tariff.name || `${tariff.hours} ${lessonsWord(tariff.hours)}`,
      books: Math.min(3, idx + 1),
      tariff,
    }));
  }

  return cards;
};

const hoursOf = (tariff) => {
  const show = Number(tariff.show_hours) || Number(tariff.hours) || 0;
  const bonus = Number(tariff.bonus_hours) || 0;

  return { show, bonus, total: Number(tariff.hours) || show };
};

const tariffLabel = (tariff) => {
  const { show, bonus } = hoursOf(tariff);
  const lessons = `${show} ${lessonsWord(show)}${bonus > 0 ? ` + ${bonus} у подарунок` : ''}`;

  return tariff.name ? `${tariff.name} · ${lessons}` : lessons;
};

const PayV2 = ({ invoice, tariffs, fullCourse, selectedHours, onSelect, methods, setOpenModal, loadingBtn, setLoadingBtn, setLink, isDemo, notice }) => {
  const [showAll, setShowAll] = useState(!fullCourse);
  const methodsRef = useRef(null);
  const tariffsRef = useRef(null);
  const flag = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('finance_course'));
      const productId = JSON.parse(localStorage.getItem('product_id'));

      return stored && String(stored.product_id) === String(productId) ? stored.image || '' : '';
    } catch (e) {
      return '';
    }
  }, []);
  const scrollToTariffs = useRef(false);

  useEffect(() => {
    if (!showAll || !scrollToTariffs.current) return;

    scrollToTariffs.current = false;
    requestAnimationFrame(() => scrollIntoViewBelowHeader(tariffsRef.current));
  }, [showAll]);

  const cards = useMemo(() => buildCards(tariffs, fullCourse), [tariffs, fullCourse]);
  const others = useMemo(
    () => tariffs.filter((tariff) => !cards.some((card) => card.tariff === tariff)),
    [tariffs, cards]
  );
  const selectedTariff = tariffs.find((tariff) => hoursOf(tariff).total === Number(selectedHours)) || null;
  const perLesson = selectedTariff ? Math.round(Number(selectedTariff.total_price) / (hoursOf(selectedTariff).total || 1)) : null;

  const choose = (hours) => {
    onSelect(hours);
    setTimeout(() => scrollIntoViewBelowHeader(methodsRef.current), 350);
  };

  return (
    <div className="pv2">
      <article className="fv2-card pv2-invoice">
        <span className="pv2-invoice__date">{invoice.created_at}</span>
        <div className="pv2-invoice__name">
          {(flag || invoice.image) && <img src={flag || invoice.image} alt="" width="40" height="27" decoding="async" />}
          <b>{courseTitle(invoice.nomenclature_name)}</b>
        </div>
        <div className="av2-doc">
          <span className="av2-doc__label">Рахунок-договір:</span>
          {invoice.document_url
            ? <button type="button" className="av2-doc__link" onClick={() => setLink(invoice.document_url)}>{invoice.document_number}</button>
            : <span className="av2-doc__link">{invoice.document_number}</span>}
        </div>
        {(invoice.counteragent_full_name || invoice.customer_counteragent_full_name) && (
          <dl className="pv2-parties">
            {invoice.counteragent_full_name && (
              <div className="pv2-parties__row">
                <dt>Замовник:</dt>
                <dd>{invoice.counteragent_full_name}</dd>
              </div>
            )}
            {invoice.customer_counteragent_full_name && (
              <div className="pv2-parties__row">
                <dt>Отримувач послуг:</dt>
                <dd>{invoice.customer_counteragent_full_name}</dd>
              </div>
            )}
          </dl>
        )}
      </article>

      {fullCourse && (
        <article className="fv2-card pv2-reco">
          <div className="pv2-reco__head">
            <span className="pv2-reco__icon"><CapIcon /></span>
            <div className="pv2-reco__main">
              <span className="v2-chip">Рекомендований тариф</span>
              <h3 className="pv2-reco__title">
                {fullCourse.hours} {lessonsWord(fullCourse.hours)} — {formatMoney(fullCourse.total_price)} грн
              </h3>
              <p className="pv2-reco__text">Рекомендуємо: найвигідніший тариф до кінця курсу.</p>
            </div>
          </div>

          <button
            type="button"
            className={`v2-btn ${Number(selectedHours) === Number(fullCourse.hours) ? 'v2-btn--violet' : 'v2-btn--green'}`}
            onClick={() => choose(Number(fullCourse.hours))}
          >
            <CardIcon />
            {Number(selectedHours) === Number(fullCourse.hours) ? 'Обрано: до кінця курсу' : 'Оплатити до кінця курсу'}
            <ArrowRight />
          </button>

          <span className="pv2-reco__or">або</span>

          <button
            type="button"
            className={`v2-btn v2-btn--outline ${showAll ? 'is-active' : ''}`.trim()}
            aria-expanded={showAll}
            onClick={() => {
              scrollToTariffs.current = !showAll;
              setShowAll((value) => !value);
            }}
          >
            Обрати інший тариф
            <ArrowRight />
          </button>
        </article>
      )}

      {showAll && cards.length > 0 && (
        <section className="pv2-tariffs" ref={tariffsRef}>
          <div className="pv2-tariffs__head">
            <h3 className="pv2-tariffs__title">Всі тарифи</h3>
            {perLesson && (
              <span className="pv2-tariffs__price">
                <InfoIcon />
                Вартість 1 уроку: {formatMoney(perLesson)} грн
              </span>
            )}
          </div>

          <div className="pv2-tariffs__grid">
            {cards.map((card) => {
              const { show, bonus, total } = hoursOf(card.tariff);
              const isOn = Number(selectedHours) === total;

              return (
                <button
                  type="button"
                  key={card.key}
                  className={`pv2-tariff ${isOn ? 'is-on' : ''}`.trim()}
                  aria-pressed={isOn}
                  onClick={() => choose(total)}
                >
                  <span className="pv2-tariff__radio" aria-hidden="true"></span>
                  <span className="pv2-tariff__title">{card.title}</span>
                  <span className="pv2-tariff__hours">
                    {show} {lessonsWord(show)}{bonus > 0 ? ` + ${bonus} у подарунок` : ''}
                  </span>
                  <BooksIcon className="pv2-tariff__books" count={card.books} />
                  <b className="pv2-tariff__sum">{formatMoney(card.tariff.total_price)} грн</b>
                  <span className="pv2-tariff__per">{formatMoney(card.tariff.price)} грн за урок</span>
                  <span className={`v2-btn v2-btn--sm ${isOn ? 'v2-btn--green' : 'v2-btn--outline'}`}>{isOn ? 'Обрано' : 'Обрати'}</span>
                </button>
              );
            })}
          </div>

          {others.length > 0 && (
            <div className="pv2-others">
              <h4 className="pv2-others__title">Інші тарифи</h4>
              {others.map((tariff, idx) => {
                const { total } = hoursOf(tariff);
                const isOn = Number(selectedHours) === total;

                return (
                  <button
                    type="button"
                    key={`o${idx}`}
                    className={`pv2-other ${isOn ? 'is-on' : ''}`.trim()}
                    aria-pressed={isOn}
                    onClick={() => choose(total)}
                  >
                    <span className="pv2-tariff__radio" aria-hidden="true"></span>
                    <span className="pv2-other__name">{tariffLabel(tariff)}</span>
                    <span className="pv2-other__sum">
                      <b>{formatMoney(tariff.total_price)} грн</b>
                      {!Number(tariff.is_special) && <small>{formatMoney(tariff.price)} грн за урок</small>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {notice}

      <div ref={methodsRef}>
        {Number(selectedHours) > 0 && (
          isDemo ? (
            <p className="pv2-demo">Тестовий режим: тут з’являться способи оплати. Справжня оплата для тестового студента не проводиться.</p>
          ) : (
            <div className="finances-pay__btns pay-btns pv2-methods">
              {methods.map((method) => (
                <FinancesPayBtns
                  key={method.type}
                  method={method}
                  setOpenModal={setOpenModal}
                  loadingBtn={loadingBtn}
                  setLoadingBtn={setLoadingBtn}
                />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

PayV2.propTypes = {
  invoice: PropTypes.object.isRequired,
  tariffs: PropTypes.array.isRequired,
  fullCourse: PropTypes.object,
  selectedHours: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  methods: PropTypes.array,
  setOpenModal: PropTypes.func.isRequired,
  loadingBtn: PropTypes.bool,
  setLoadingBtn: PropTypes.func.isRequired,
  setLink: PropTypes.func.isRequired,
  isDemo: PropTypes.bool,
  notice: PropTypes.node,
};

PayV2.defaultProps = {
  fullCourse: null,
  selectedHours: 0,
  methods: [],
  loadingBtn: false,
  isDemo: false,
  notice: null,
};

export default PayV2;
